import React, { useState, useEffect, useRef } from "react";
import { backendApi } from "./api/backendApi";
import { Bot, Send, Sparkles, User as UserIcon } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

// ---------------------------------------------------------------------
//  THEME TOKENS — matches the flat editorial surface used across HRMS
//  (kept in sync with the tokens in EmployeeDashboard.jsx)
// ---------------------------------------------------------------------
const T = {
  green: '#1A3C2B',
  greenDeep: '#122B1F',
  greenTint: '#EDF3EF',
  bg: '#F6F6F3',
  card: '#FFFFFF',
  line: '#E7E6E0',
  lineSoft: '#F0EFEA',
  ink: '#15181C',
  body: '#3F4650',
  muted: '#6B7280',
  faint: '#9CA3AF',
};

// ---------------------------------------------------------------------
//  PRIMITIVES (mirrors the primitives used on the Dashboard page)
// ---------------------------------------------------------------------
const Panel = ({ title, icon: Icon, children }) => (
  <section className="hrms-card flex flex-col">
    {title && (
      <div className="flex items-center gap-2 px-5 pt-4 pb-3" style={{ borderBottom: `1px solid ${T.lineSoft}` }}>
        {Icon && <Icon className="w-[15px] h-[15px] flex-shrink-0" style={{ color: T.green }} strokeWidth={2} />}
        <h3 className="text-[15px] font-bold" style={{ color: T.ink }}>{title}</h3>
      </div>
    )}
    <div className="flex-1 min-w-0">{children}</div>
  </section>
);

const SUGGESTIONS = [
  "Summarize this month's leave trends",
  "Draft a performance review reminder",
  "What's our attendance policy?",
];

export default function AIAssistant({ initialPrompt = "" }) {
  return (
    <>
      <style>{`
        .hrms-ai-textarea, .hrms-ai-textarea:focus, .hrms-ai-textarea:focus-visible {
          border: none !important;
          outline: none !important;
          box-shadow: none !important;
        }
      `}</style>
      <AIAssistantInner initialPrompt={initialPrompt} />
    </>
  );
}

function AIAssistantInner({ initialPrompt = "" }) {
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content: "Hello! I'm your AI HR Assistant. I can help you with employee queries, analytics, insights, and HR operations. How can I assist you today?"
    }
  ]);
  const [input, setInput] = useState(initialPrompt);
  const [isLoading, setIsLoading] = useState(false);
  const scrollRef = useRef(null);

  // Seed the box when the dashboard hands over a question or a suggestion chip.
  useEffect(() => {
    if (initialPrompt) setInput(initialPrompt);
  }, [initialPrompt]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isLoading]);

  const handleSend = async (overrideText) => {
    const text = (overrideText ?? input).trim();
    if (!text) return;

    const userMessage = { role: "user", content: text };
    setMessages(prev => [...prev, userMessage]);
    setInput("");
    setIsLoading(true);

    try {
      const result = await backendApi.integrations.Core.InvokeLLM({
        prompt: `You are an AI HR Assistant for WebIdeal HRMS. Answer the following HR-related question: ${text}
        
Provide helpful, professional, and concise responses about HR topics including:
- Employee management
- Leave policies
- Performance management
- Recruitment
- Compliance
- HR analytics

Keep responses practical and actionable.`,
        add_context_from_internet: false
      });

      const assistantMessage = {
        role: "assistant",
        content: typeof result === 'string' ? result : (result.response || result.content || JSON.stringify(result))
      };
      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error("AI Assistant Error:", error);
      const errorMessage = {
        role: "assistant",
        content: "I apologize, but I'm currently unable to process your request. The AI service may be temporarily unavailable. Please try again in a moment."
      };
      setMessages(prev => [...prev, errorMessage]);
    }

    setIsLoading(false);
  };

  return (
    <div className="p-6 md:p-8" style={{ background: T.bg, minHeight: '100%' }}>
      <div>
        {/* Header row */}
        <div className="flex items-center gap-3 mb-6">
          <span className="inline-flex w-11 h-11 rounded-xl items-center justify-center flex-shrink-0" style={{ background: T.greenTint }}>
            <Bot className="w-5 h-5" style={{ color: T.green }} strokeWidth={2} />
          </span>
          <div>
            <h2 className="text-[19px] font-bold tracking-tight" style={{ color: T.ink }}>AI HR Assistant</h2>
            <p className="text-[12px] mt-0.5" style={{ color: T.muted }}>Powered by advanced AI for intelligent HR insights</p>
          </div>
        </div>

        <Panel title="AI Chat" icon={Sparkles}>
          <div className="flex flex-col" style={{ height: 'calc(100vh - 260px)', minHeight: '520px' }}>
            {/* Messages */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-5 space-y-4">
              <AnimatePresence initial={false}>
                {messages.map((message, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className="max-w-[75%] lg:max-w-2xl rounded-2xl px-4 py-3"
                      style={
                        message.role === 'user'
                          ? { background: T.green, color: '#fff' }
                          : { background: T.lineSoft, color: T.ink, border: `1px solid ${T.line}` }
                      }
                    >
                      <div className="flex items-center gap-1.5 mb-1.5">
                        {message.role === 'assistant'
                          ? <Bot className="w-3.5 h-3.5" style={{ color: T.green }} strokeWidth={2} />
                          : <UserIcon className="w-3.5 h-3.5" style={{ color: 'rgba(255,255,255,0.85)' }} strokeWidth={2} />}
                        <span className="text-[11px] font-semibold" style={message.role === 'user' ? { color: 'rgba(255,255,255,0.85)' } : { color: T.muted }}>
                          {message.role === 'assistant' ? 'AI Assistant' : 'You'}
                        </span>
                      </div>
                      <p className="text-[13px] leading-relaxed whitespace-pre-wrap">{message.content}</p>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>

              {isLoading && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex justify-start">
                  <div className="rounded-2xl px-4 py-3" style={{ background: T.lineSoft, border: `1px solid ${T.line}` }}>
                    <div className="flex items-center gap-2">
                      <div className="flex gap-1">
                        <span className="w-1.5 h-1.5 rounded-full animate-bounce" style={{ background: T.faint, animationDelay: '0ms' }} />
                        <span className="w-1.5 h-1.5 rounded-full animate-bounce" style={{ background: T.faint, animationDelay: '150ms' }} />
                        <span className="w-1.5 h-1.5 rounded-full animate-bounce" style={{ background: T.faint, animationDelay: '300ms' }} />
                      </div>
                      <span className="text-[11.5px]" style={{ color: T.muted }}>AI is thinking...</span>
                    </div>
                  </div>
                </motion.div>
              )}

              {messages.length === 1 && !isLoading && (
                <div className="pt-2">
                  <p className="text-[11px] font-semibold uppercase tracking-wide mb-2" style={{ color: T.faint }}>Try asking</p>
                  <div className="flex flex-wrap gap-2">
                    {SUGGESTIONS.map((s) => (
                      <button
                        key={s}
                        onClick={() => handleSend(s)}
                        className="px-3 py-1.5 rounded-full text-[12px] font-medium transition-colors"
                        style={{ background: T.greenTint, color: T.green }}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Input */}
            <div className="px-5 py-4" style={{ borderTop: `1px solid ${T.lineSoft}`, background: T.greenTint }}>
              <div className="flex items-end gap-3">
                <div
                  className="flex-1 flex items-end gap-2 rounded-xl px-4 py-2.5 transition-shadow"
                  style={{
                    border: `1.5px solid ${T.green}`,
                    background: T.card,
                    boxShadow: '0 2px 10px rgba(26,60,43,0.10)',
                  }}
                >
                  <Sparkles className="w-4 h-4 flex-shrink-0 mb-1" style={{ color: T.green }} strokeWidth={2} />
                  <textarea
                    placeholder="Ask me anything about HR, employees, analytics..."
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSend();
                      }
                    }}
                    rows={2}
                    autoFocus
                    className="hrms-ai-textarea flex-1 resize-none text-[13px] focus:outline-none bg-transparent border-0 focus:ring-0 focus:shadow-none"
                    style={{ color: T.ink, border: 'none', outline: 'none', boxShadow: 'none', appearance: 'none', forcedColorAdjust: 'none' }}
                  />
                </div>
                <button
                  onClick={() => handleSend()}
                  disabled={isLoading || !input.trim()}
                  className="hrms-btn-primary inline-flex items-center justify-center w-11 h-11 flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ boxShadow: '0 2px 10px rgba(26,60,43,0.18)' }}
                >
                  <Send className="w-4 h-4" strokeWidth={2} />
                </button>
              </div>
              <p className="text-[11px] mt-2.5 text-center" style={{ color: T.muted }}>
                AI responses are generated based on HR best practices. Always verify critical information.
              </p>
            </div>
          </div>
        </Panel>
      </div>
    </div>
  );
}